import { Log, User, UserManager as OIDCUserManager, UserManagerSettings } from 'oidc-client-ts';
import { IsNullOrWhiteSpace } from './Micro';
// --------------------------------------------------------------------------------------------------
export interface UserManagerRedirectState {
    urlSearch: string;
    locationHRef: string;
}
// --------------------------------------------------------------------------------------------------
export class UserManager {
    // --------------------------------------------------------------------------------------------------
    private static _instance: UserManager = null;
    public static GetInstance(): UserManager {
        if (this._instance == null)
            this._instance = new UserManager();
        return this._instance;
    };
    // --------------------------------------------------------------------------------------------------
    private userManager: OIDCUserManager;
    private user: User = null;
    // --------------------------------------------------------------------------------------------------
    get baseUrl(): string { return window.location.origin };
    // --------------------------------------------------------------------------------------------------
    constructor() {
        Log.setLogger(console);
        Log.setLevel(Log.INFO);

        let logoutRedirection = this.baseUrl;
        if (!IsNullOrWhiteSpace((window as any).LogoutRedirection))
            logoutRedirection = (window as any).LogoutRedirection;

        const settings: UserManagerSettings = {
            authority: (window as any).Authority,
            client_id: (window as any).ClientId,
            scope: (window as any).ScopeId,
            redirect_uri: `${this.baseUrl}/emerald/login/signin_callback`,
            silent_redirect_uri: `${this.baseUrl}/emerald/login/silent_renew`,
            post_logout_redirect_uri: logoutRedirection,
            response_type: 'code',
            filterProtocolClaims: true,
            loadUserInfo: false,
            automaticSilentRenew: true
        };
        this.userManager = new OIDCUserManager(settings);

        // Save user information
        this.userManager.events.addUserLoaded(user => {
            console.error("User loaded: ", user);
            this.user = user;
        });
    }
    // --------------------------------------------------------------------------------------------------
    public async init() {
        try {
            const userManager = UserManager.GetInstance();
            const urlPath = window.location.pathname;

            if (urlPath?.endsWith('/login/signin_callback') ||
                urlPath?.endsWith('/login/signin_callback.html')) {
                this.user = await userManager.loginCb();
                const state = this.user?.state as UserManagerRedirectState;
                window.location.replace(`${state?.locationHRef ?? this.baseUrl}${state?.urlSearch}`);
            }
            else if (
                urlPath?.endsWith('/login/silent_renew') ||
                urlPath?.endsWith('/login/silent_renew.html')) {
                userManager
                    .loginSilentCb()
                    .catch(e => console.error(e));
            }
            else {
                let user = await this.userManager.getUser();
                if (user == null) {
                    console.error("Failed to get user");
                    const loginRedirect = (window as any).LoginRedirection as string;

                    if (!IsNullOrWhiteSpace(loginRedirect)) {
                        // Check if we are already login in server
                        try { this.user = await this.userManager.signinSilent(); }
                        catch (e) { console.error(e); }

                        // If we are not then redirect to the proxy server
                        if (this.user == null)
                            window.location.replace(loginRedirect);
                    } else
                        await this.login();

                } else if (user.expired) {
                    console.error("Old user expired: ", user);
                    this.user = await this.userManager.signinSilent();
                } else
                    this.user = user;
            }
        } catch (e) {
            console.error(e);
            // not login request it
            await this.login();
        }

        console.error("User: ", this.user);
        return this.user;
    }
    // --------------------------------------------------------------------------------------------------
    public getAccessToken() {
        return this.user?.access_token ?? null;
    }
    // --------------------------------------------------------------------------------------------------
    public login(): Promise<void> {
        return this.userManager.signinRedirect({
            state: {
                urlSearch: window.location.search,
                locationHRef: window.location.href,
            } as UserManagerRedirectState
        });
    }
    // --------------------------------------------------------------------------------------------------
    public loginCb(): Promise<User> {
        return this.userManager.signinRedirectCallback(window.location.href);
    }
    // --------------------------------------------------------------------------------------------------
    public loginSilentCb(): Promise<void> {
        return this.userManager.signinSilentCallback();
    }
    // --------------------------------------------------------------------------------------------------
    public logout(): Promise<void> {
        this.user = null;
        return this.userManager.signoutRedirect();
    }
    // --------------------------------------------------------------------------------------------------
}